import React from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BirthdayPicker from '../shared/BirthdayPicker';

// 地址類型選項
const addressTypeOptions = [
  { value: 'home', label: '住家' },
  { value: 'work', label: '工作場所' },
  { value: 'hospital', label: '醫院' },
  { value: 'other', label: '其他' }
];

const FamilySection = ({
  formData,
  formErrors,
  onFamilyMemberChange,
  onUsePrimaryAddress,
  onAddFamilyMember,
  onRemoveFamilyMember,
  simplified = false
}) => {
  return (
    <>
      {/* 標題 */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mt: 2 }}>
          家人資訊
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          如需為家人一併請示，請填寫家人資訊（最多3人）
        </Typography>
      </Grid>

      {/* 家人列表 */}
      {formData.familyMembers?.map((member, index) => (
        <Grid item xs={12} key={index}>
          <Accordion sx={{ mb: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'background.default',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant="subtitle1">
                  家人 {index + 1}: {member.name || '未命名'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFamilyMember(index);
                  }}
                  color="error"
                  sx={{ mr: 1 }}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* 姓名 */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    required={!simplified}
                    fullWidth
                    label="姓名"
                    value={member.name || ''}
                    onChange={(e) => onFamilyMemberChange(index, 'name', e.target.value)}
                    error={Boolean(formErrors[`familyMembers.${index}.name`])}
                    helperText={formErrors[`familyMembers.${index}.name`]}
                  />
                </Grid>

                {/* 性別（下拉） */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(formErrors[`familyMembers.${index}.gender`])}>
                    <InputLabel>性別</InputLabel>
                    <Select
                      value={member.gender || ''}
                      onChange={(e) => onFamilyMemberChange(index, 'gender', e.target.value)}
                      label="性別"
                    >
                      <MenuItem value="male">男</MenuItem>
                      <MenuItem value="female">女</MenuItem>
                      {/* 2026-06-24（WS5 D6）：移除「其他」——other 用途改為系統「待填」標記，前台不該選到 */}
                    </Select>
                    {formErrors[`familyMembers.${index}.gender`] && (
                      <FormHelperText>{formErrors[`familyMembers.${index}.gender`]}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* 家人農曆生日（Follow-up patch #5 D6：移除外部標題，BirthdayPicker 內部 default 接手） */}
                <Grid item xs={12}>
                  <BirthdayPicker
                    year={member.birthYear || ''}
                    month={member.birthMonth || ''}
                    day={member.birthDay || ''}
                    isLeapMonth={member.lunarIsLeapMonth || false}
                    onChange={({ year, month, day, isLeapMonth }) => {
                      // Change C / 階段 2.2：永遠 lunar，calendarType 不再從 onChange 解構（內部恆為 'lunar'）
                      onFamilyMemberChange(index, {
                        calendarType: 'lunar',
                        birthYear: year,
                        birthMonth: month,
                        birthDay: day,
                        lunarIsLeapMonth: isLeapMonth
                      });
                    }}
                    errors={{
                      year: formErrors[`familyMembers.${index}.birthYear`],
                      month: formErrors[`familyMembers.${index}.birthMonth`],
                      day: formErrors[`familyMembers.${index}.birthDay`]
                    }}
                  />
                </Grid>

                {/* 地址同上核取方框 */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={member.usePrimaryAddress || false}
                        onChange={(e) => onUsePrimaryAddress && onUsePrimaryAddress(index, e.target.checked)}
                        disabled={!formData.addresses?.[0]?.address}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        同上（使用主客戶地址）
                      </Typography>
                    }
                  />
                  {!formData.addresses?.[0]?.address && (
                    <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
                      請先填寫主客戶地址
                    </Typography>
                  )}
                </Grid>

                {/* 地址 */}
                <Grid item xs={12} sm={8}>
                  <TextField
                    required={!simplified}
                    fullWidth
                    label="地址"
                    value={member.address || ''}
                    onChange={(e) => onFamilyMemberChange(index, 'address', e.target.value)}
                    error={Boolean(formErrors[`familyMembers.${index}.address`])}
                    helperText={formErrors[`familyMembers.${index}.address`]}
                  />
                </Grid>

                {/* 地址類型 */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <FormLabel component="legend">地址類別</FormLabel>
                    <RadioGroup
                      row
                      value={member.addressType || 'home'}
                      onChange={(e) => onFamilyMemberChange(index, 'addressType', e.target.value)}
                    >
                      {addressTypeOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size="small" />}
                          label={option.label}
                          sx={{ minWidth: 'auto', mr: 1 }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}

      {/* 新增家人按鈕 */}
      {(!formData.familyMembers || formData.familyMembers.length < 3) && (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddFamilyMember}
            sx={{ mt: 1 }}
          >
            新增家人
          </Button>
        </Grid>
      )}
    </>
  );
};

export default FamilySection;
